import { LayoutTemplateRegistry } from '@src/editor/models/layout/LayoutTemplateRegistry';

import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { Scene } from '@src/editor/models/layout/Scene';

/**
 * Strips auto-initialized textPosition from basic-template scene karaoke
 * keyframes where the values match the global KaraokeConfig. This ensures
 * those scenes correctly inherit from the global config rather than being
 * treated as manually overridden.
 *
 * Mutates scene data in place. Intended to run during project load, before
 * undo history is initialized.
 *
 * Returns the number of scenes modified.
 */
export function migrateBasicTemplateTextPositions(
  scenes: readonly Scene[],
  defaultConfig: KaraokeConfig,
): number {
  const registry = LayoutTemplateRegistry.getInstance();
  let modified = 0;

  for (const scene of scenes) {
    const template = registry.getTemplate(scene.layout.layoutTemplateId);
    if (!template || template.type !== 'basic') continue;

    const binding = scene.layout.bindings.karaoke;
    if (!binding?.transformKeyframes?.[0]?.textPosition) continue;

    const tp = binding.transformKeyframes[0].textPosition;
    const matches =
      (tp.x ?? undefined) === (defaultConfig.x ?? undefined) &&
      (tp.y ?? undefined) === (defaultConfig.y ?? undefined) &&
      (tp.x_alignment ?? undefined) ===
        (defaultConfig.x_alignment ?? undefined);

    if (matches) {
      delete binding.transformKeyframes[0].textPosition;
      modified++;
    }
  }

  return modified;
}
